'use client';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

type OverlayState = {
  isOpen: boolean;
  /** `opener` gets focus back when the overlay closes. */
  open: (opener: HTMLElement | null) => void;
  close: () => void;
  /** One id for every button that opens it, for `aria-controls`. */
  panelId: string;
  openerRef: React.RefObject<HTMLElement | null>;
};

export type SortFilterOverlay = OverlayState & {
  /** The title of the panel that wants a loan bar button; null when none does. */
  panelTitle: string | null;
  /** Whether a button on the loan bar is on screen, for the panel's own to give way to. */
  hasBarButton: boolean;
  /** For the panel, while it renders and wants a loan bar button. Returns the undo. */
  registerPanel: (title: string) => () => void;
  /** For a button on the loan bar, while it is on screen. Returns the undo. */
  registerBarButton: () => () => void;
};

const useOverlayState = (): OverlayState => {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();
  const openerRef = useRef<HTMLElement | null>(null);
  const open = useCallback((opener: HTMLElement | null) => {
    openerRef.current = opener;
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  return useMemo(
    () => ({ isOpen, open, close, panelId, openerRef }),
    [isOpen, open, close, panelId],
  );
};

const SortFilterOverlayContext = createContext<SortFilterOverlay | null>(null);

/**
 * Lets a button outside the panel open its overlay. The panel renders in the
 * results row and the loan bar is portalled in from a block, so neither is
 * inside the other: the state lives above both. It holds nothing about the page
 * itself; the panel and the buttons report in as they mount, so what is shown
 * follows what is actually rendered.
 */
export const SortFilterOverlayProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const state = useOverlayState();
  const [panelTitle, setPanelTitle] = useState<string | null>(null);
  const [barButtons, setBarButtons] = useState(0);

  const registerPanel = useCallback((title: string) => {
    setPanelTitle(title);
    return () => setPanelTitle(null);
  }, []);
  const registerBarButton = useCallback(() => {
    setBarButtons((count) => count + 1);
    return () => setBarButtons((count) => count - 1);
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      panelTitle,
      hasBarButton: barButtons > 0,
      registerPanel,
      registerBarButton,
    }),
    [state, panelTitle, barButtons, registerPanel, registerBarButton],
  );

  return (
    <SortFilterOverlayContext.Provider value={value}>
      {children}
    </SortFilterOverlayContext.Provider>
  );
};

/** The shared overlay, or `null` where no provider is mounted. */
export const useSortFilterOverlay = (): SortFilterOverlay | null =>
  useContext(SortFilterOverlayContext);

/**
 * What the panel opens and closes: the shared state when a provider is mounted,
 * its own otherwise, so the panel still works on its own. While `forBarButton`
 * it reports itself and its title, so a button on the loan bar shows only for a
 * panel that is really there and wants one.
 */
export const usePanelOverlay = (title: string, forBarButton: boolean) => {
  const shared = useSortFilterOverlay();
  const own = useOverlayState();
  const registerPanel = shared?.registerPanel;

  useEffect(() => {
    if (!forBarButton || !registerPanel) return;
    return registerPanel(title);
  }, [forBarButton, registerPanel, title]);

  return { ...(shared ?? own), hasBarButton: shared?.hasBarButton ?? false };
};
